import { Inject, Injectable, NgZone, DOCUMENT } from '@angular/core';

import QrScanner from 'qr-scanner';
import { Observable, Subject } from 'rxjs';

import { IQrCodeScanningConfig, IQrCodeScanResult, IPoint, IQrCodeScanningConfigVideoElement } from './interfaces';
import { ConfigurationStorageService, DEFAULT_APP_CONFIG } from '../../configuration-storage.service';
import { CameraInversionMode, PreferredCamera } from '../../interfaces';
import { delay } from '../../shared.service';

// export type InversionMode = 'original' | 'invert' | 'both';

/**
 * Service to start QR Camera scanner.
 *
 * This service is not injected into root,
 * because it should be able to store multiple instance of different owners.
 */
@Injectable({
    providedIn: 'root'
})
export class CameraQrScanService {
    private qrScanner: QrScanner;
    private config: IQrCodeScanningConfig;
    private scanResult$ = new Subject<IQrCodeScanResult>();
    private scanError$ = new Subject<string | Error>();
    private lastCameraId = '';
    private lastScannedAt = 0;
    private lastScanResult: QrScanner.ScanResult;

    constructor(@Inject(DOCUMENT) private document: Document, private configService: ConfigurationStorageService,
        private ngZone: NgZone
    ) { }

    /**
     * @returns true if already intialized, otherwise false
     */
    isInitialized(): boolean {
        return !!this.qrScanner;
    }

    /**
     * Starts scanning - this will cause stream from selected camera to be shown in the video element
     * Notice that calling this immediatelly after destroyScannerInstance() followed by init() will not have effect
     * A slight
     *
     * @param config Configuration for the initialization - either videoElement or videoElementSelector is required
     */
    async start(config: IQrCodeScanningConfig): Promise<void> {
        if (!this.isVideoElementAttached(config)) {
            this.init(config);

            // It turns out that if start() is called immediatelly after stop(), destroy() and init(), the video stream is not shown
            // Give it some milliseconds before starting
            await delay(
                this.configService.getAppConfig()?.delayBetweenInitAndStartCamera
                ?? DEFAULT_APP_CONFIG.delayBetweenInitAndStartCamera
            );
        }
        this.qrScanner?.start();
    }

    /**
     * Switches to another camera. Can be called before or after start()
     *
     * @param preferredCamera PreferredCamera ("user" or "environment") or camera device id
     */
    async setPreferredCamera(preferredCamera: PreferredCamera | string): Promise<void> {
        this.lastCameraId = preferredCamera;
        this.qrScanner.setCamera(preferredCamera);
    }

    /**
     * Toggles between "user" and "environment" camera
     *
     * @returns `PreferredCamera` the new selected camera
     */
    async toggleCamera(): Promise<PreferredCamera> {
        const newCamera = this.lastCameraId === PreferredCamera.user ? PreferredCamera.environment : PreferredCamera.user;
        await this.setPreferredCamera(newCamera);
        return newCamera;
    }

    /**
    * Toggles between "user" and "environment" camera
    *
    * @returns `PreferredCamera` the new selected camera
    */
    async setInversionMode(mode: CameraInversionMode): Promise<boolean> {
        if (!this.qrScanner || mode === CameraInversionMode.doNotUse) {
            return;
        }
        await this.qrScanner.setInversionMode(mode);
        return true;
    }

    /**
     * Stops scanning. This will stop camera stream but will not destroy the scanner instance allowing next start() to be faster
     */
    stop(): void {
        this.qrScanner?.stop();
        if (this.isAlwaysDestroyCameraInstance()) {
            this.destroy();
        }
    }

    /**
     * Gets the observable which emits when scan result is available
     *
     * @returns Observable<IQrCodeScanResult>
     */
    getScanResultObservable(): Observable<IQrCodeScanResult> {
        return this.scanResult$.asObservable();
    }

    /**
     * Returns observable which emits when scan error occurs.
     * If QR code is not detected it will also emit
     *
     * @returns Observable<string | Error> with the error
     */
    getScanErrorObservable(): Observable<string | Error> {
        return this.scanError$.asObservable();
    }

    /**
     * Destroys the camera instance. Use it when camera is not needed anymore (until the next initialization)
     */
    destroy(): void {
        this.destroyScannerInstance();
    }

    /**
     * Initializes the scanner instance. Do not call it if already initialized - use isInitialized() to check
     * Must precede calls to other functions like start(), setPreferredCamera(), stop()
     *
     * @param config Configuration for the initialization - either videoElement or videoElementSelector is required
     * @returns true if initialization suceeded, false otherwise (for example if video element cannot be found)
     */
    private init(config: IQrCodeScanningConfig): boolean {
        this.config = this.createConfigWithDefaults(config);
        this.lastCameraId = this.config.preferredCamera;
        const videoElement: HTMLVideoElement = this.config.videoElement ?? this.document.querySelector(this.config.videoElementSelector);
        if (!videoElement) {
            return false;
        }
        this.destroy();
        this.qrScanner = new QrScanner(
            videoElement,
            // every [someSubject$].next should be run under ngZone.run()
            // because the QrScanner makes the code run outside NgZone
            decodeScanResult => this.ngZone.run(() => this.processScanResult(decodeScanResult)),
            {
                onDecodeError: err => this.ngZone.run(() => this.scanError$.next(err)),
                maxScansPerSecond: this.config.maxScansPerSecond,
                preferredCamera: this.config.preferredCamera,
                returnDetailedScanResult: true,
                highlightScanRegion: this.config.highlightScanRegion,
                highlightCodeOutline: this.config.highlightCodeOutline
            }
        );
        this.setInversionMode(config.cameraInversionMode || CameraInversionMode.doNotUse);
        return true;
    }

    private processScanResult(scanResult: QrScanner.ScanResult): void {
        if (scanResult.data === this.lastScanResult?.data) {
            const diff = Date.now() - this.lastScannedAt;
            if (diff < this.config.ignoreSameCodesForMs) {
                // Same code has been scanned for small period of time - ignore it
                return;
            }
        }
        this.lastScannedAt = Date.now();
        this.lastScanResult = scanResult;
        this.scanResult$.next(this.createScanResult(scanResult));
    }

    private createScanResult(qrScanResult: QrScanner.ScanResult): IQrCodeScanResult {
        const scanResult: IQrCodeScanResult = {
            data: qrScanResult.data,
            cornerPoints: qrScanResult.cornerPoints.map(point => ({ x: point.x, y: point.y } as IPoint))
        };
        return scanResult;
    }

    private destroyScannerInstance(): void {
        if (this.qrScanner) {
            this.qrScanner.destroy();
            this.qrScanner = null;
        }
    }

    private createConfigWithDefaults(config: IQrCodeScanningConfig): IQrCodeScanningConfig {
        const configWithDefaults = { ...config };
        configWithDefaults.maxScansPerSecond ||= 10;
        configWithDefaults.preferredCamera ||= PreferredCamera.user;
        if (configWithDefaults.highlightScanRegion === undefined) {
            configWithDefaults.highlightScanRegion = true;
        }
        if (configWithDefaults.highlightCodeOutline === undefined) {
            configWithDefaults.highlightCodeOutline = true;
        }
        return configWithDefaults;
    }

    private isAlwaysDestroyCameraInstance() {
        return this.configService.getAppConfig().alwaysDestroyQrCameraInstance;
    }

    /**
     * Checks if the current `CameraQrScanService` is currently using the same video element from `videoParam`
     */
    private isVideoElementAttached(videoParam: IQrCodeScanningConfigVideoElement) {
        const videoElement = this.getVideoElementFromVideoParam(videoParam);
        if (videoElement == null) {
            console.error('Attached video element is not exist', videoParam);
            return false;
        }
        return this.isInitialized()
            && this.config
            // comparing objects will return true if both are the same instances,
            // because they are having same memory address
            && videoElement === this.getVideoElementFromVideoParam(this.config)
            && videoElement === this.qrScanner?.$video;
    }

    private getVideoElementFromVideoParam(videoParam: IQrCodeScanningConfigVideoElement): HTMLVideoElement | null {
        if (videoParam?.videoElement) {
            return videoParam?.videoElement;
        }
        if (videoParam?.videoElementSelector) {
            return document.querySelector(videoParam?.videoElementSelector);
        }
        return null;
    }
}
